package com.myfield.app.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.myfield.app.style.TextSelectionStyle
import com.myfield.app.util.BackgroundGradient
import com.myfield.app.util.Constants.ACME_REGULAR
import com.myfield.app.util.Constants.LATO_REGULAR
import com.myfield.app.util.Constants.SUPPORT_EMAIL
import com.myfield.app.util.TextGradient
import com.myfield.app.util.Theme
import com.varabyte.kobweb.compose.css.BackgroundClip
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@OptIn(DelicateApi::class)
@Composable
@Page
fun PrivacyPolicyPage() {
    val breakpoint = rememberBreakpoint()
    val largeBreakpoint = remember(breakpoint) {
        breakpoint > Breakpoint.LG
    }

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundImage(BackgroundGradient)
                .padding(
                    leftRight = if (breakpoint > Breakpoint.LG) 120.px
                    else if (breakpoint == Breakpoint.LG) 100.px
                    else if (breakpoint > Breakpoint.MD) 70.px
                    else if (breakpoint == Breakpoint.MD) 50.px
                    else 16.px
                ),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(if (largeBreakpoint) 800.px else 600.px)
                    .padding(bottom = 100.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Main Title
                H1(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 20.px, top = if (largeBreakpoint) 50.px else 20.px)
                        .backgroundImage(TextGradient)
                        .background { this.clip(BackgroundClip.Text) }
                        .color(Color.transparent)
                        .fontFamily(ACME_REGULAR)
                        .fontSize(if (largeBreakpoint) 4.cssRem else 2.4.cssRem)
                        .lineHeight(1.2)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Політика конфіденційності")
                }

                // Subtitle Information
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.TextLowVisibility.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.4)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Дата набрання чинності: 06.05.2026")
                }

                Surface(modifier = Modifier.height(20.px)) {}

                // Introduction
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text("MyField («ми», «наш» або «нас») поважає вашу конфіденційність. Ця Політика конфіденційності пояснює, як ми збираємо, використовуємо та захищаємо вашу інформацію під час використання нашого мобільного застосунку («Застосунок»). Правова підстава для обробки ваших даних — виконання договору (надання послуг застосунку) та ваша згода.")
                }

                // Section 1
                SectionTitle("1. Інформація, яку ми збираємо", largeBreakpoint)
                SectionContent("Під час реєстрації та використання MyField ми збираємо таку інформацію:")
                SubSectionTitle("Дані облікового запису:", largeBreakpoint)
                SectionContent("• Ім'я та прізвище")
                SectionContent("• Адреса електронної пошти")
                SectionContent("• Номер телефону")
                SectionContent("• Назва господарства (необов'язково)")
                SubSectionTitle("Дані автентифікації та сесії:", largeBreakpoint)
                SectionContent("• Зашифрований пароль (пароль ніколи не зберігається у відкритому вигляді)")
                SectionContent("• Токени автентифікації (токени доступу та оновлення), що зберігаються у захищеному сховищі пристрою (Keychain на iOS, EncryptedSharedPreferences на Android)")
                SectionContent("• Унікальний ідентифікатор пристрою (UUID) — генерується при першому запуску та використовується виключно для прив'язки сесій до конкретного пристрою")
                SectionContent("• Модель пристрою та версія операційної системи — використовуються для управління сесіями")
                SectionContent("• Коди підтвердження email та токени скидання пароля (тимчасові, видаляються після використання)")
                SubSectionTitle("Операційні та бізнес-дані:", largeBreakpoint)
                SectionContent("Всі дані, які ви вводите під час роботи з застосунком, зокрема:")
                SectionContent("• Поля та записи про культури")
                SectionContent("• Записи про врожай, закупівлі та продажі")
                SectionContent("• Фінансові записи (внески, виплати, платежі, баланси)")
                SectionContent("• Інформація про працівників, орендодавців та кредиторів")
                SectionContent("• Аналітичні дані, отримані з ваших операційних записів")
                SubSectionTitle("Дані підписки:", largeBreakpoint)
                SectionContent("• Статус підписки «MyField Pro» (активна/неактивна, дата закінчення, тип плану)")
                SectionContent("• Ці дані отримуються від RevenueCat на основі транзакцій App Store або Google Play")
                SubSectionTitle("Ми не збираємо:", largeBreakpoint)
                SectionContent("• Геолокацію")
                SectionContent("• Контакти")
                SectionContent("• Повідомлення або комунікаційний контент")
                SectionContent("• Дані платіжних карток або банківські реквізити")

                // Section 2
                SectionTitle("2. Як ми використовуємо вашу інформацію", largeBreakpoint)
                SectionContent("Ми використовуємо зібрану інформацію для:")
                SectionContent("• Створення та управління вашим обліковим записом")
                SectionContent("• Автентифікації та забезпечення безпечного доступу до застосунку")
                SectionContent("• Надсилання транзакційних листів (підтвердження акаунту, скидання пароля, сповіщення безпеки)")
                SectionContent("• Забезпечення основних функцій застосунку: управління полями, фінансовий облік та агроаналітика")
                SectionContent("• Відображення інформації вашого профілю в застосунку")
                SectionContent("• Зв'язку з вами у разі потреби (з використанням номера телефону або електронної пошти)")
                SectionContent("• Управління вашою підпискою «MyField Pro» та перевірки доступу до преміум-функцій")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent("Ваші дані не використовуються для реклами, відстеження або профілювання.")

                // Section 3
                SectionTitle("3. Передача даних третім особам", largeBreakpoint)
                SectionContent("Ми не продаємо, не передаємо в оренду та не надаємо ваші персональні дані третім особам у комерційних цілях.")
                SubSectionTitle("Ми використовуємо таких постачальників послуг виключно для роботи застосунку:", largeBreakpoint)
                SectionContent("• Сервіс доставки електронної пошти — використовується для надсилання кодів підтвердження та сповіщень безпеки. Цьому сервісу передається лише ваша електронна адреса та ім'я.")
                SectionContent("• Firebase Crashlytics (Google) — використовується для збору звітів про збої та діагностики технічних проблем. Crashlytics може збирати інформацію про пристрій, унікальний ідентифікатор встановлення Firebase та стан застосунку на момент збою. Він не збирає дані вашого акаунту або бізнес-дані. Політика конфіденційності Google: https://firebase.google.com/support/privacy")
                SectionContent("• Firebase Analytics (Google) — наразі не використовується. У майбутньому може бути активовано для збору знеособленої статистики використання застосунку. Про це буде повідомлено в оновленій версії цієї Політики.")
                SectionContent("• RevenueCat — використовується для управління підписками та обробки покупок. RevenueCat отримує анонімний ідентифікатор користувача (UUID вашого облікового запису MyField), інформацію про стан підписки та дані транзакцій від App Store або Google Play. RevenueCat не отримує ваше ім'я, електронну пошту або бізнес-дані. Політика конфіденційності RevenueCat: https://www.revenuecat.com/privacy")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent("Ваші дані можуть зберігатися та оброблятися на серверах у різних країнах. Ми забезпечуємо відповідний рівень захисту даних незалежно від місця їх зберігання.")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent("Жодні інші сторонні сервіси не отримують ваші дані.")

                // Section 4
                SectionTitle("4. Безпека даних", largeBreakpoint)
                SectionContent("Ми вживаємо розумних технічних та організаційних заходів для захисту ваших даних, зокрема:")
                SectionContent("• Паролі шифруються і ніколи не зберігаються у відкритому вигляді")
                SectionContent("• Автентифікація використовує підписані токени з терміном дії")
                SectionContent("• Сесії керуються на рівні пристрою")
                SectionContent("• Доступ до ваших даних завжди вимагає автентифікації")
                SectionContent("• Технічні журнали (логи) сервера можуть містити внутрішні ідентифікатори облікових записів (UUID) для цілей безпеки та діагностики. Ці дані зберігаються обмежений час та доступні лише авторизованому персоналу.")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent("Зберігається лише мінімально необхідна інформація для забезпечення функціональності застосунку.")

                // Section 5
                SectionTitle("5. Зберігання даних", largeBreakpoint)
                SectionContent("Ваші дані зберігаються протягом усього часу існування вашого облікового запису.")
                SubSectionTitle("У разі видалення облікового запису:", largeBreakpoint)
                SectionContent("• Ваші активні сесії (токени автентифікації) видаляються негайно та безповоротно")
                SectionContent("• Ваш акаунт та пов'язані дані будуть видалені з наших систем після обробки запиту на видалення")
                SectionContent("• Якщо ви хочете запросити негайне постійне видалення всіх ваших даних — зверніться до нас за адресою з розділу «Зв'яжіться з нами»")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent("Тимчасові дані, такі як коди підтвердження email та токени скидання пароля, автоматично видаляються після використання або закінчення терміну дії.")

                // Section 6
                SectionTitle("6. Ваші права", largeBreakpoint)
                SectionContent("Ви маєте право:")
                SectionContent("• Отримати доступ до персональних даних, які ми зберігаємо про вас")
                SectionContent("• Вимагати виправлення неточних даних")
                SectionContent("• Вимагати видалення вашого облікового запису та всіх пов'язаних даних")
                SectionContent("• Скасувати активні сесії на конкретних пристроях або на всіх пристроях одночасно")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent("Щоб скористатися будь-яким із цих прав, зверніться до нас, використовуючи контактні дані з розділу «Зв'яжіться з нами».")

                // Section 7
                SectionTitle("7. Вікові обмеження", largeBreakpoint)
                SectionContent("MyField призначений для використання особами віком від 16 років. Ми не збираємо свідомо персональні дані осіб, які не досягли цього віку. Якщо вам стало відомо, що особа віком до 16 років надала нам свої дані — зверніться до нас, і ми видалимо їх негайно.")

                // Section 8
                SectionTitle("8. Відмова від відповідальності", largeBreakpoint)
                SectionContent("Ми не несемо відповідальності за фінансові або господарські рішення, прийняті на підставі даних або аналітики застосунку. Користувачам рекомендується вести незалежний облік критично важливих ділових та фінансових даних.")

                // Section 9
                SectionTitle("9. Застосовне право", largeBreakpoint)
                SectionContent("Ця Політика конфіденційності регулюється чинним законодавством. Якщо ви використовуєте застосунок за межами України, ви також можете підпадати під дію місцевого законодавства про захист даних, зокрема GDPR для користувачів з ЄС.")

                // Section 10
                SectionTitle("10. Зміни до цієї Політики конфіденційності", largeBreakpoint)
                SectionContent("Ми можемо час від часу оновлювати цю Політику конфіденційності. Про будь-які суттєві зміни буде повідомлено в застосунку або електронною поштою. Дата набрання чинності у верхній частині цієї політики завжди відображатиме останню версію.")

                // Section 11
                SectionTitle("11. Зв'яжіться з нами", largeBreakpoint)
                SectionContent("Якщо у вас є запитання, запити або занепокоєння щодо цієї Політики конфіденційності або способу обробки ваших даних, зверніться до нас:")
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(topBottom = 10.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text(SUPPORT_EMAIL)
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(30.px)) {}
    H2(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 20.px)
            .color(Theme.Text.rgb)
            .fontFamily(LATO_REGULAR)
            .fontSize(if (largeBreakpoint) 1.8.cssRem else 1.6.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SubSectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(15.px)) {}
    H3(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.Text.rgb)
            .fontFamily(LATO_REGULAR)
            .fontSize(if (largeBreakpoint) 1.4.cssRem else 1.3.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SectionContent(
    text: String,
) {
    P(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.TextLowVisibility.rgb)
            .fontFamily(LATO_REGULAR)
            .fontSize(1.1.cssRem)
            .lineHeight(1.6)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}